import { Alert, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import {
  useGetOrderDetailsQuery,
  useRefundOrderPaymentMutation,
} from 'api/ordering-service/ordering-service';
import FormDrawer from 'components/drawers/form-drawer/form-drawer.component';
import OrderRefundForm from 'components/forms/order-refund-form/order-refund-form.component';
import { useOrderRefundDrawerForm } from 'components/forms/order-refund-form/order-refund-form.hooks';
import OrderInformation from 'pages/analytics/salesreport/order-details-info.component';
import OrderSummaryInformation from 'pages/analytics/salesreport/order-summary-info.component';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const OrderRefundDrawer = ({
  open,
  close,
  drawerData,
  refetchOnOpen = false,
}) => {
  const { orderDetails: orderData } = drawerData ?? {};
  const { t } = useTranslation();

  const {
    currentData: orderDetails,
    isFetching: getorderDetailsLoading,
    refetch: refetchOrderDetails,
  } = useGetOrderDetailsQuery(orderData?.orderId ?? skipToken);

  useEffect(() => {
    if (refetchOnOpen && open && orderData?.orderId) {
      refetchOrderDetails();
    }
  }, [refetchOnOpen, open, orderData?.orderId, refetchOrderDetails]);

  const { form } = useOrderRefundDrawerForm({
    refundableAmount:
      (orderDetails?.orderTotal?.total -
        orderDetails?.orderTotal?.refundSummary?.totalRefundAmount) /
      1000,
  });

  const [refundOrderPaymentMutation, { isLoading: updateRefundLoading }] =
    useRefundOrderPaymentMutation();

  const cancel = () => {
    form.reset();
    close();
  };

  const onValid = async data => {
    let requestBody = {
      isPartialRefund: data.fullAmount === 'partialAmount' ? true : false,
      orderId: orderData?.orderId,
      refundReason: data.refundReason,
      refundAmount: data.refundAmount * 1000,
      emailReceipt: data.emailReceipt,
    };
    await refundOrderPaymentMutation(requestBody);
    form.reset();
    close();
  };

  return (
    <FormDrawer
      open={open}
      data-testid="OrderRefundDetails.Drawer"
      title={t('drawer.orderRefundDetailsDrawer.title')}
      isLoading={getorderDetailsLoading || updateRefundLoading}
      formMethods={form}
      onValid={onValid}
      submitButtonLabel={t('drawer.refundDrawer.refundAction.submit')}
      onClose={cancel}
      cancel={cancel}
    >
      <Typography variant="h2" gutterBottom mb={3} mt={4}>
        {t('forms.fields.refundForm.title')}
      </Typography>
      <Alert severity="info" icon={false} elevation={2}>
        <OrderRefundForm orderData={orderData} orderDetails={orderDetails} />
      </Alert>
      <OrderInformation
        orderDetails={orderDetails}
        orderInfoHeader={
          <Typography variant="h2" gutterBottom mb={3} mt={4}>
            {t('drawer.orderDetailsDrawer.orderInformation')}
          </Typography>
        }
      />
      <OrderSummaryInformation
        orderDetails={orderDetails}
        customGridColumns={12}
      />
    </FormDrawer>
  );
};

export default OrderRefundDrawer;
